import type { QueryClient } from '@tanstack/react-query';
import { LoginStatus } from '@/entities/user/user/@x/session';

export interface AuthSessionState {
  loginStatus: LoginStatus;
  reauthenticating: boolean;
}

export const AUTH_SESSION_KEY = ['auth-session'] as const;
export const initialSessionState: AuthSessionState = {
  loginStatus: LoginStatus.pending,
  reauthenticating: false,
};

export const getSessionState = (queryClient: QueryClient): AuthSessionState =>
  queryClient.getQueryData<AuthSessionState>(AUTH_SESSION_KEY) ??
  initialSessionState;

export const setSessionState = (
  queryClient: QueryClient,
  state: AuthSessionState,
) => {
  // Loaders can update this query before React mounts its observer.
  queryClient.setQueryDefaults(AUTH_SESSION_KEY, {
    cacheTime: Infinity,
    staleTime: Infinity,
  });
  return queryClient.setQueryData<AuthSessionState>(AUTH_SESSION_KEY, state);
};

export const clearSessionData = async (queryClient: QueryClient) => {
  const filters = {
    predicate: (query: { queryKey: readonly unknown[] }) =>
      query.queryKey[0] !== AUTH_SESSION_KEY[0],
  };
  await queryClient.cancelQueries(filters);
  queryClient.removeQueries(filters);
};
// Shared by the form and the synchronous guard against duplicate submissions.
export const canAuthenticate = (status: LoginStatus) =>
  [LoginStatus.pending, LoginStatus.unauthorized, LoginStatus.expired].includes(
    status,
  );

export const transitionSession = (
  queryClient: QueryClient,
  loginStatus: LoginStatus,
) => {
  const previous = getSessionState(queryClient);
  const reauthenticating =
    loginStatus === LoginStatus.authenticating
      ? previous.loginStatus === LoginStatus.expired
      : [LoginStatus.preparing, LoginStatus.preparationError].includes(
            loginStatus,
          )
        ? previous.reauthenticating
        : false;
  return setSessionState(queryClient, { loginStatus, reauthenticating });
};

// Failed credentials and an expired server session return to the same entry point.
export const requireAuthentication = (queryClient: QueryClient) => {
  const state = getSessionState(queryClient);
  return transitionSession(
    queryClient,
    state.reauthenticating ||
      [LoginStatus.login, LoginStatus.expired].includes(state.loginStatus)
      ? LoginStatus.expired
      : LoginStatus.unauthorized,
  );
};

export const startAuthentication = (queryClient: QueryClient) => {
  const previous = getSessionState(queryClient);
  if (!canAuthenticate(previous.loginStatus)) return undefined;
  const session = transitionSession(queryClient, LoginStatus.authenticating);
  return { previous, session };
};
